import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import useAgency from 'features/Agency/useAgency';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DialogAddPricingView from '../DialogAddPricingView';
import ChargePricingListFilterView from './ChargePricingListFilterView';
import AppConstants from 'general/constants/AppConstants';

ChargePricingListView.propTypes = {};

const defaultColumnIds = [
  'PRICING_CODE',
  'USER',
  'CHARGE_POINT',
  'INVESTOR',
  'OPERATOR',
  'PRICING_MODE',
  'PRICE',
  'FREE_OVERCHARGE_DURATION',
  'OVERCHARGE_PRICE',
];

function ChargePricingListView(props) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { operatorList, operatorOptions } = useAgency();
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [filter, setFilter] = useState(ChargeHelper.Filter.chargePricingList);
  const [chargePricingList, setChargePricingList] = useState([]);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    let params = _.pickBy(filter, (value) => value !== null && value !== undefined && value !== '');
    if (user?.role === ChargeConstants.UserRole.AGENCY && Utils.isValid(user?.agencyId)) {
      params.agencyId = user?.agencyId;
    }
    ChargeHelper.API.requestGetChargePricingList(params)
      .then((response) => {
        setChargePricingList(response?.pricings);
        setTotal(response?.total);
      })
      .finally(() => { });
  }, [filter, user]);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'PRICING_CODE',
        name: UIHelper.buildTableColumn('Mã định giá'),
        selector: (row) => row.pricingCode ?? row.pricingId,
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'USER',
        name: UIHelper.buildTableColumn('Người đặt giá'),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60">{row?.user?.fullname}</span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CHARGE_POINT',
        name: UIHelper.buildTableColumn('Trụ sạc'),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
            {row?.point?.pointCode}
          </span>
        ),
        minWidth: '180px',
        style: tableStyle.columns.link,
      },
      {
        id: 'INVESTOR',
        name: UIHelper.buildTableColumn('Nhà đầu tư'),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
            {row?.agency?.name}
          </span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'OPERATOR',
        name: UIHelper.buildTableColumn('Nhà điều hành'),
        selector: (row) => (
          <span className="cursor-pointer hover-opacity-60" onClick={() => { }}>
            {row?.operator?.name}
          </span>
        ),
        minWidth: '160px',
        style: tableStyle.columns.link,
      },
      {
        id: 'PRICING_MODE',
        name: UIHelper.buildTableColumn('Phương thức tính giá'),
        selector: (row) =>
          row?.pricingMode
            ? _.find(
              ChargeHelper.Data.chargePricingModeOptions,
              (item) => item?.value === row?.pricingMode
            )?.name
            : '',
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PRICE',
        name: UIHelper.buildTableColumn('Giá sạc', 'VNĐ/kWh'),
        selector: (row) => {
          if (row?.pricingMode === ChargeConstants.ChargePricingMode.FLAT) {
            return (
              <span>
                {row?.flatPrice !== null ? Utils.formatNumber(row?.flatPrice) : 'Mặc định'}
              </span>
            );
          } else if (row?.pricingMode === ChargeConstants.ChargePricingMode.HOURS) {
            return (
              <span>
                BT: {Utils.formatNumber(row?.normalPrice)}, CĐ {Utils.formatNumber(row?.peakPrice)},
                TĐ: {Utils.formatNumber(row?.offPeakPrice)}
              </span>
            );
          }
          return <span>{'Mặc định'}</span>;
        },
        right: true,
        minWidth: '260px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'FREE_OVERCHARGE_DURATION',
        name: UIHelper.buildTableColumn('Thời gian chờ miễn phí', 'Phút'),
        selector: (row) =>
          row?.pricing !== null ? Utils.formatNumber(row?.freeOverchargeDuration / 60) : 'Mặc định',
        right: true,
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'OVERCHARGE_PRICE',
        name: UIHelper.buildTableColumn('Phí sạc quá giờ', 'VNĐ/phút'),
        selector: (row) =>
          row?.pricing !== null ? Utils.formatNumber(row?.overchargePrice) : 'Mặc định',
        right: true,
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, [user]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns?.map((row) => row?.id).filter(Boolean) || []
  );
  const windowSize = useWindowSize();
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const dropdownInvestorEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="investorId"
        name="investorId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedInvestor?.name ?? 'Chọn nhà đầu tư'}
          </button>
        }
        selectedValue={selectedInvestor}
        subTitleType="email"
        dropdownMenuItems={[{ name: 'Nhà đầu tư NQ', role: 'INVESTOR' }]}
        getSubItems={async (item) => {
          return AgencyHelper.API.requestGetAgencyList({
            role: AgencyConstants.AgencyRole.INVESTOR,
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.agencies?.map((row) => ({
                id: row?.agencyId,
                name: row?.name,
                item: row,
                email: row?.email,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedInvestor(value);
          setSelectedChargePoint(null);
          ChargeHelper.Filter.chargePricingList = {
            ...ChargeHelper.Filter.chargePricingList,
            agencyId: value?.id,
            operatorId: '',
            pointId: '',
            page: 0,
          };
          handleRefresh();
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Nhà đầu tư NQ',
          role: 'INVESTOR',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedInvestor(null);
              setSelectedChargePoint(null);
              ChargeHelper.Filter.chargePricingList = {
                ...ChargeHelper.Filter.chargePricingList,
                agencyId: '',
                operatorId: '',
                pointId: '',
                page: 0,
              };
              handleRefresh();
            })}
          </div>
        }
      />
    );
  }, [selectedInvestor]);
  const [selectedChargePoint, setSelectedChargePoint] = useState(null);
  const dropdownChargePointEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="pointId"
        name="pointId"
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
            type="button"
          >
            {selectedChargePoint?.name ?? 'Chọn trụ sạc'}
          </button>
        }
        selectedValue={selectedChargePoint}
        dropdownMenuItems={[{ name: 'Trụ sạc', role: 'CHARGE_POINT' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.requestGetChargePointList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.chargePoints?.map((row) => ({
                id: row?.pointId,
                name: row?.pointCode,
                item: row,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectedChargePoint(value);
          setSelectedInvestor(null);
          ChargeHelper.Filter.chargePricingList = {
            ...ChargeHelper.Filter.chargePricingList,
            agencyId: '',
            operatorId: '',
            pointId: value?.id,
            page: 0,
          };
          handleRefresh();
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchFormClassName="mx-3 my-1 search-form-border"
        ignoreConfirmClick={true}
        defaultValue={{
          name: 'Trụ sạc',
          role: 'CHARGE_POINT',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectedChargePoint(null);
              setSelectedInvestor(null);
              ChargeHelper.Filter.chargePricingList = {
                ...ChargeHelper.Filter.chargePricingList,
                agencyId: '',
                operatorId: '',
                pointId: '',
                page: 0,
              };
              handleRefresh();
            })}
          </div>
        }
      />
    );
  }, [selectedChargePoint]);

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    const isMine = row?.agencyId === user?.agencyId;
    const actions = [];
    if (isAdmin || isMine) {
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.pricingUpdate.code)) {
        // chinh sua
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            handleAddNew(row);
          },
        });
      }
      if (Utils.checkPermission(permissionList, AppConstants.permissionCode.pricingDelete.code)) {
        // xoa
        actions.push({
          text: 'Xoá',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            handleDelete(row);
          },
        });
      }
    }
    return actions;
  };

  const handleRefresh = () => {
    setFilter({ ...ChargeHelper.Filter.chargePricingList });
  };
  const handleDelete = (row) => {
    ChargeHelper.UI.Pricing.showDialogConfirmDeletePricing(row, () => {
      handleRefresh();
    });
  };
  const handleAddNew = (row) => {
    AppCustomDialogHelper.show(
      !!row ? 'Sửa giá' : 'Thêm giá',
      <DialogAddPricingView
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh();
        }}
      />,
      'lg',
      false,
      true
    );
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
            {/* title */}
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Định giá sạc điện
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(total)} mức giá
                </span>
              </span>
            </div>

            {/* actions */}
            {Utils.checkPermission(permissionList, AppConstants.permissionCode.pricingCreate.code) &&
              UIHelper.buildGreenButton(
                'Thêm mới',
                handleAddNew,
                <i className="fa-regular fa-circle-plus" />
              )}
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              {windowSize.width >= 768 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Tìm kiếm')}
                  <KTFormSearch
                    name="search"
                    placeholder="Nhập để tìm kiếm..."
                    value={filter.q}
                    onSubmit={(text) => {
                      ChargeHelper.Filter.chargePricingList = {
                        ...ChargeHelper.Filter.chargePricingList,
                        q: text,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                  />
                </div>
              )}
              {/* loc theo nha dieu hanh */}
              {windowSize.width >= 1120 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Nhà điều hành')}
                  <KTBSDropdown
                    name="operatorId"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(
                          operatorList,
                          (item) =>
                            item?.operatorId === ChargeHelper.Filter.chargePricingList?.operatorId
                        )?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={ChargeHelper.Filter.chargePricingList?.operatorId}
                    dropdownMenuItems={[{ name: 'Tất cả', value: '' }, ...operatorOptions]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      setSelectedChargePoint(null);
                      setSelectedInvestor(null);
                      ChargeHelper.Filter.chargePricingList = {
                        ...ChargeHelper.Filter.chargePricingList,
                        operatorId: value,
                        agencyId: '',
                        pointId: '',
                        page: 0,
                      };
                      handleRefresh();
                    }}
                    isDropdownForm={true}
                  />
                </div>
              )}
              {/* loc theo nha dau tu NQ */}
              {windowSize.width >= 1368 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Nhà đầu tư NQ')}
                  {dropdownInvestorEl}
                </div>
              )}
              {/* loc theo tru sac */}
              {windowSize.width >= 1616 && (
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Trụ sạc')}
                  {dropdownChargePointEl}
                </div>
              )}
              {/* thiet lap cot */}
              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                <ButtonFilterDropdown
                  dropdownContent={
                    <ChargePricingListFilterView
                      onCancel={() => {
                        handleRefresh();
                      }}
                      onApply={() => {
                        handleRefresh();
                      }}
                      initialSelectedInvestor={selectedInvestor}
                      initialSelectedPoint={selectedChargePoint}
                      onSelectedInvestorChange={(value) => {
                        setSelectedInvestor(value);
                      }}
                      onSelectedPointChange={(value) => {
                        setSelectedChargePoint(value);
                      }}
                    />
                  }
                />
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  resetDefaultTableColumn={resetDefaultTableColumn}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                  saveChanges={saveChanges}
                />
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={chargePricingList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>
          </div>

          {/* pagination */}
          <div className="px-4 mt-2">
            <KTPagination
              currentPage={filter.page + 1}
              rowCount={total}
              rowsPerPage={filter.limit}
              currentItemInPageCount={chargePricingList?.length}
              onChangePage={(newPage) => {
                const iNewPage = parseInt(newPage);
                ChargeHelper.Filter.chargePricingList = {
                  ...ChargeHelper.Filter.chargePricingList,
                  page: iNewPage - 1,
                };
                handleRefresh();
              }}
              onChangeRowsPerPage={(newRowsPerPage) => {
                const iNewRowsPerPage = parseInt(newRowsPerPage);
                ChargeHelper.Filter.chargePricingList = {
                  ...ChargeHelper.Filter.chargePricingList,
                  limit: iNewRowsPerPage,
                  page: 0,
                };
                handleRefresh();
              }}
              loading={false}
              color={KTPaginationColor.success}
              appendText="mức giá"
            />
          </div>
        </div>,
        false
      )}
    </div>
  );
}

export default ChargePricingListView;
